import React, { useEffect, useMemo, useState } from 'react';
import ConfirmDialog from './ConfirmDialog';
import {
  NOTIFY_REASONS,
  NOTIFY_OTHER_KEY,
  NOTIFY_NOTE_MAX_LENGTH,
  reasonsFromStored,
} from '../constants/notifyReasons';
import { FIELD_LABEL_CLASS } from '../constants/inputs';

/**
 * NOTIFY USER — the reason picker.
 *
 * Product owner, 2026-08-11: *"Maybe the action should just be **Notify User**
 * with a selection box of options like out of stock, drastic price difference,
 * longer than expected lead times etc so the buyer/supervisor can just tick a
 * box and then leave a note if they wish"*.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT IS A `ConfirmDialog` BODY SLOT, NOT A FIFTH MODAL
 * ─────────────────────────────────────────────────────────────────────────────
 * The focus trap, Escape, overlay-`mousedown` close and focus RETURN all come
 * from `components/ConfirmDialog.js`, which already gets those four right;
 * CLAUDE.md forbids hand-rolling another modal beside it in terms. What lives
 * HERE is the vocabulary — reasons, the note, the cap — because a
 * `reasons`/`onReasonsChange` prop on the shared dialog would be this feature's
 * model wearing a shared primitive's name.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔑 ADDING A REASON IS ONE ARRAY ENTRY, AND THAT IS THE BRIEF'S STANDING
 *    REQUIREMENT RATHER THAN A NICETY
 * ─────────────────────────────────────────────────────────────────────────────
 * This component MAPS OVER `NOTIFY_REASONS` and never names a key except
 * `NOTIFY_OTHER_KEY`. A fourth reason is one entry in
 * `constants/notifyReasons.js` and one in `server/helpers/notifyReasons.js`;
 * nothing here, in the route, or in the schema changes. **Never write a reason
 * label or key into this file.**
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🚨 VALIDATION MIRRORS THE SERVER AND IS A CONVENIENCE, NEVER A GATE
 * ─────────────────────────────────────────────────────────────────────────────
 * `POST /api/orders/:id/items/:itemId/notify` re-checks all three conditions and
 * answers a distinct `code` for each — `notify_reasons_required`,
 * `notify_note_required`, `notify_note_too_long`. This file exists so the user
 * never meets one of them, not so the server can stop checking.
 *
 *  - **At least one reason.** Confirm is disabled with none ticked.
 *  - **`other` makes the note REQUIRED.** The key is a UI affordance meaning
 *    "not on the list, here is a box", so a blank note there is a notification
 *    carrying no reason at all.
 *  - 📏 **The note is CAPPED AT `NOTIFY_NOTE_MAX_LENGTH` AND REFUSED, NEVER
 *    TRUNCATED.** `maxLength` on the textarea plus a live counter is what makes
 *    the server's 400 unreachable through the UI — which is the only reason the
 *    server is allowed to refuse rather than silently cut. 🪤 Do not "improve"
 *    this by slicing the value in the change handler: truncation loses the
 *    buyer's words with no signal, and the last third of a sentence is where the
 *    ask usually is.
 *
 * 🔑 **THE CLIENT NEVER COMPOSES THE MESSAGE.** This dialog sends `reasons` and
 * `note` and nothing else. The sentence that lands in the requester's
 * notification bell is built server-side from stored values and the caller's
 * identity, because a client-supplied string would be arbitrary-text injection
 * into another user's inbox, signed with the caller's name.
 *
 * ♿ The counter is `aria-live="polite"` so a screen-reader user hears the
 * remaining budget rather than discovering the cap by being silently stopped at
 * it. It is NOT `assertive` — it would interrupt every keystroke.
 *
 * ⚠️ jsdom applies no stylesheet and composites nothing, so everything about how
 * this LOOKS — the checkbox alignment, the counter's colour at the limit, the
 * panel's width on a phone — is a BROWSER check.
 *
 * Props:
 *   open        — render nothing unless true.
 *   item        — the order item being flagged. Read for its description (shown
 *                 so the buyer can see which line they are about to flag) and,
 *                 when already flagged, to seed the ticked reasons and the note.
 *   busy        — in-flight; disables every control.
 *   error       — a string to show above the buttons (the server's own message).
 *   onSubmit({ reasons, note }) — the caller performs the POST.
 *   onCancel    — close without sending.
 */
export default function NotifyUserDialog({
  open,
  item,
  busy = false,
  error = null,
  onSubmit,
  onCancel,
}) {
  const [selected, setSelected] = useState([]);
  const [note, setNote] = useState('');

  // 🔑 SEEDED FROM THE ITEM ON EACH OPEN, so re-notifying an already-flagged
  //    line opens on what it currently says rather than on a blank form — the
  //    buyer is usually AMENDING (adding a second reason, correcting the note),
  //    and making them retype it invites them to send less than they knew.
  // 🪤 Keyed on `open` as well as the item so a second open of the SAME line
  //    re-seeds rather than showing whatever was half-typed and abandoned last
  //    time. A dialog that remembers a cancelled edit is a dialog that sends one.
  useEffect(() => {
    if (!open) return;
    setSelected(reasonsFromStored(item?.notify_reasons));
    setNote(typeof item?.notify_note === 'string' ? item.notify_note : '');
  }, [open, item]);

  const otherTicked = selected.includes(NOTIFY_OTHER_KEY);
  const trimmedNote = note.trim();

  // Mirrors the route's own order of checks. `noteTooLong` is unreachable while
  // `maxLength` holds, and is computed anyway so the button cannot enable if a
  // future edit removes the attribute.
  const invalid = useMemo(() => {
    if (selected.length === 0) return true;
    if (otherTicked && trimmedNote === '') return true;
    if (trimmedNote.length > NOTIFY_NOTE_MAX_LENGTH) return true;
    return false;
  }, [selected.length, otherTicked, trimmedNote]);

  const toggle = (key) => {
    setSelected((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const remaining = NOTIFY_NOTE_MAX_LENGTH - note.length;

  const submit = () => {
    if (invalid || busy) return;
    // 🔑 Send the keys in the frozen list's CANONICAL order, not in click order.
    //    The server stores the CSV it is given, so click order would make two
    //    identical flags differ by the sequence the buyer happened to tick them
    //    in — and `reasonsFromStored` would then re-order them on read anyway,
    //    so the row and the request would disagree for no reason.
    const reasons = NOTIFY_REASONS.filter((r) => selected.includes(r.key)).map((r) => r.key);
    onSubmit?.({ reasons, note: trimmedNote });
  };

  const label = (item && (item.description || item.part_number)) || 'this line';

  return (
    <ConfirmDialog
      open={open}
      title="Notify User"
      message={
        <>
          Tell the requester about a problem with{' '}
          <strong className="text-gray-800 dark:text-gray-200">{label}</strong>. They will get a
          notification, and the line stays marked until you clear it.
        </>
      }
      confirmLabel="Send notification"
      cancelLabel="Cancel"
      busy={busy}
      confirmDisabled={invalid}
      onConfirm={submit}
      onCancel={onCancel}
    >
      <fieldset disabled={busy} className="border-0 p-0 m-0">
        <legend className={FIELD_LABEL_CLASS}>
          What is the problem?
        </legend>

        <div className="space-y-1">
          {NOTIFY_REASONS.map((reason) => (
            // ⚠️ The ⓘ-inside-a-label trap does not apply here (there is no
            //    InfoTip), but the same structural rule does: the whole label
            //    is the hit area, which is what gives a 44px-tall row a target
            //    the width of the panel rather than a 16px box.
            <label
              key={reason.key}
              className="flex items-center gap-2 min-h-[44px] md:min-h-0 md:py-1 px-2 -mx-2 rounded cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
            >
              <input
                type="checkbox"
                checked={selected.includes(reason.key)}
                onChange={() => toggle(reason.key)}
                data-testid={`notify-reason-${reason.key}`}
                className="h-4 w-4 shrink-0 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-800 dark:text-gray-200">{reason.label}</span>
            </label>
          ))}
        </div>

        <div className="mt-3">
          <label
            htmlFor="notify-note"
            className={FIELD_LABEL_CLASS}
          >
            Note{' '}
            {otherTicked ? (
              <span className="text-red-600 dark:text-red-400">(required)</span>
            ) : (
              <span className="font-normal text-gray-500 dark:text-gray-400">(optional)</span>
            )}
          </label>
          <textarea
            id="notify-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            // 📏 REFUSE, NEVER TRUNCATE — the attribute stops the keystroke, so
            //    nothing the buyer typed is ever silently discarded.
            maxLength={NOTIFY_NOTE_MAX_LENGTH}
            rows={3}
            data-testid="notify-note"
            aria-describedby="notify-note-counter"
            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-3 py-2 text-sm resize-y focus:ring-2 focus:ring-blue-500"
          />
          <div
            id="notify-note-counter"
            data-testid="notify-note-counter"
            aria-live="polite"
            // 🪤 `text-gray-500` is BARE with no `dark:` pair on purpose:
            //    `index.css` delivers the dark value for it with `!important`,
            //    so a pair would compile and be INERT. The amber at the limit
            //    IS paired — `text-amber-700` is not in that override table.
            className={`mt-1 text-xs ${
              remaining === 0
                ? 'text-amber-700 dark:text-amber-400 font-medium'
                : 'text-gray-500'
            }`}
          >
            {remaining} character{remaining === 1 ? '' : 's'} left
          </div>
        </div>

        {otherTicked && trimmedNote === '' && (
          <p className="mt-2 text-xs text-red-600 dark:text-red-400" role="alert">
            Add a note to say what the problem is.
          </p>
        )}
      </fieldset>

      {error && (
        <p className="mt-3 text-sm text-red-600 dark:text-red-400" role="alert" data-testid="notify-error">
          {error}
        </p>
      )}
    </ConfirmDialog>
  );
}
